import { DataStoreType } from "../stores/dataStore";
import loadTextBucketIntoCache from "./backend/text_buckets/loadTextBucketIntoCache";
import sanitizeTableData from "./sanitizeTableData";

/**
 * Result of a merge operation including any conflicts that need resolution
 */
export interface MergeResult {
    data: DataStoreType["data"];
    conflicts: {
        localNewer: DataStoreType["data"];
        cloudNewer: DataStoreType["data"];
    };
    removeFromCloud: Deleted;
}

/**
 * Merge two TableData objects, resolving conflicts by preferring the latest updated
 * @param localData The local TableData object
 * @param cloudData The cloud TableData object
 * @returns A MergeResult object containing the merged data and any conflicts that need resolution
 */
export function MergeTableData(
    localDataBefore: DataStoreType["data"],
    cloudDataBefore: DataStoreType["data"]
): MergeResult {
    const localData = sanitizeTableData(localDataBefore);
    const cloudData = sanitizeTableData(cloudDataBefore);

    function mergeUniqueArrays<T>(
        array1: T[],
        array2: T[]
    ): { array: T[]; changesMade: boolean } {
        const set1 = new Set(array1 || []);
        const set2 = new Set(array2 || []);
        const combinedArray = new Set([...Array.from(set1), ...Array.from(set2)]);
        const newArray = Array.from(combinedArray);
        const changesMade =
            newArray.length !== (array1 || []).length ||
            newArray.length !== (array2 || []).length;
        return { array: newArray, changesMade };
    }

    const notesChangesMade = mergeUniqueArrays(
        localData.deleted?.notes,
        cloudData.deleted?.notes
    );
    const foldersChangesMade = mergeUniqueArrays(
        localData.deleted?.folders,
        cloudData.deleted?.folders
    );
    const tagsChangesMade = mergeUniqueArrays(
        localData.deleted?.tags,
        cloudData.deleted?.tags
    );

    const mergedDeleted: Deleted = {
        notes: mergeUniqueArrays(
            localData.deleted?.notes,
            cloudData.deleted?.notes
        ).array,
        folders: mergeUniqueArrays(
            localData.deleted?.folders,
            cloudData.deleted?.folders
        ).array,
        tags: mergeUniqueArrays(
            localData.deleted?.tags,
            cloudData.deleted?.tags
        ).array,
    };

    // conflicts where localData is newer
    const conflicts: MergeResult["conflicts"] = {
        localNewer: {
            notes: {},
            folders: {},
            tags: {},
            deleted:{
                notes: notesChangesMade.changesMade ? notesChangesMade.array : undefined,
                folders: foldersChangesMade.changesMade ? foldersChangesMade.array : undefined,
                tags: tagsChangesMade.changesMade ? tagsChangesMade.array : undefined,
            }
        },
        cloudNewer: {
            notes: {},
            folders: {},
            tags: {},
        },
    };

    // Merge notes, preferring the latest updated
    function mergeItems<T extends { updatedAt?: string; dateCreated?: string }>(
        localItems: Record<string, T>,
        cloudItems: Record<string, T>,
        deletedIds: string[],
        compareField: "updatedAt" | "dateCreated",
        conflictLocal: Record<string, T>,
        conflictCloud: Record<string, T>
    ): Record<string, T> {
        const merged: Record<string, T> = {};
        const allIds = new Set([
            ...Object.keys(localItems),
            ...Object.keys(cloudItems),
        ]);

        for (const id of Array.from(allIds)) {
            const localItem = localItems[id];
            const cloudItem = cloudItems[id];

            // If id is deleted, don't merge
            if (deletedIds.includes(id)) continue;

            if (localItem && cloudItem) {
                // Both exist, compare timestamps
                const localDate = new Date(localItem[compareField]!);
                const cloudDate = new Date(cloudItem[compareField]!);

                // local is newer
                if (localDate > cloudDate) {
                    merged[id] = localItem;
                    conflictLocal[id] = localItem;
                }
                // cloud is newer
                else if (cloudDate > localDate) {
                    merged[id] = cloudItem;
                    conflictCloud[id] = localItem;
                    // Load cloud notes into cache if it's a note
                    if (compareField === "updatedAt") {
                        loadTextBucketIntoCache(id, true);
                    }
                } else {
                    // Exactly the same timestamp, no conflict
                    merged[id] = cloudItem;
                }
            } else if (localItem) {
                // Only exists in local
                merged[id] = localItem;
                conflictLocal[id] = localItem;
            } else if (cloudItem) {
                // Only exists in cloud
                merged[id] = cloudItem;
                conflictCloud[id] = localItem;
                // Load cloud notes into cache if it's a note
                if (compareField === "updatedAt") {
                    loadTextBucketIntoCache(id);
                }
            }
        }

        return merged;
    }

    // Merge notes, folders, and tags using the generic function
    const mergedNotes = mergeItems(
        localData.notes,
        cloudData.notes,
        mergedDeleted.notes,
        "updatedAt",
        conflicts.localNewer.notes,
        conflicts.cloudNewer.notes
    );

    const mergedFolders = mergeItems(
        localData.folders,
        cloudData.folders,
        mergedDeleted.folders,
        "updatedAt",
        conflicts.localNewer.folders,
        conflicts.cloudNewer.folders
    );

    const mergedTags = mergeItems(
        localData.tags,
        cloudData.tags,
        mergedDeleted.tags,
        "dateCreated",
        conflicts.localNewer.tags,
        conflicts.cloudNewer.tags
    );

    // Find what needs to be removed from cloud
    const removeFromCloud: Deleted = { notes: [], folders: [], tags: [] };
    mergedDeleted.notes.forEach((deletedNoteID) => {
        if (cloudData.notes[deletedNoteID]) {
            removeFromCloud.notes.push(deletedNoteID);
        }
    });
    mergedDeleted.folders.forEach((deletedFolderID) => {
        if (cloudData.folders[deletedFolderID]) {
            removeFromCloud.folders.push(deletedFolderID);
        }
    });
    mergedDeleted.tags.forEach((deletedTagID) => {
        if (cloudData.tags[deletedTagID]) {
            removeFromCloud.tags.push(deletedTagID);
        }
    });

    return {
        data: {
            notes: mergedNotes,
            folders: mergedFolders,
            tags: mergedTags,
            deleted: mergedDeleted,
        },
        conflicts,
        removeFromCloud,
    };
}
